<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

const actionsOpen = defineModel('actionsOpen', {
  type: Boolean,
  default: false,
});
const { updateUISettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open && !actionsOpen.value
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

const handleConversationSidebarToggle = () => {
  const shouldOpen = !isContactSidebarOpen.value;
  actionsOpen.value = false;
  updateUISettings({
    is_contact_sidebar_open: shouldOpen,
    is_copilot_panel_open: false,
  });
};

const handleCopilotSidebarToggle = () => {
  actionsOpen.value = false;
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: !isCopilotPanelOpen.value,
  });
};

const handleActionsSidebarToggle = () => {
  const shouldOpen = !actionsOpen.value;
  actionsOpen.value = shouldOpen;
  if (shouldOpen) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: handleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    data-conversation-panel-switch
    class="absolute top-36 xl:top-24 end-2 flex flex-col items-center gap-2 !z-20"
  >
    <ButtonGroup
      class="flex items-center justify-center bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full p-1.5 shadow-sm transition-shadow duration-200 hover:shadow"
    >
      <Button
        v-tooltip.top="$t('CONVERSATION_ACTIONS_PANEL.OPEN')"
        ghost
        slate
        sm
        :aria-label="$t('CONVERSATION_ACTIONS_PANEL.OPEN')"
        :aria-expanded="actionsOpen"
        aria-controls="conversation-actions-panel"
        class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
        :class="{ 'bg-n-alpha-2 active:shadow-sm': actionsOpen }"
        @click="handleActionsSidebarToggle"
      >
        <template #icon>
          <Icon
            icon="i-woot-guandalini-symbol"
            aria-hidden="true"
            class="size-5 shrink-0 text-black dark:text-white"
          />
        </template>
      </Button>
    </ButtonGroup>
    <ButtonGroup
      class="flex flex-col justify-center items-center bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow"
    >
      <Button
        v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
        ghost
        slate
        sm
        :aria-label="$t('CONVERSATION.SIDEBAR.CONTACT')"
        :aria-expanded="isContactSidebarOpen"
        class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
        :class="{
          'bg-n-alpha-2 active:shadow-sm': isContactSidebarOpen,
        }"
        icon="i-ph-user-bold"
        @click="handleConversationSidebarToggle"
      />
      <Button
        v-if="showCopilotTab"
        v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
        ghost
        slate
        sm
        class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
        :class="{
          'bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
            isCopilotPanelOpen,
        }"
        icon="i-woot-captain"
        @click="handleCopilotSidebarToggle"
      />
    </ButtonGroup>
  </div>
</template>
